import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { object, isNotEmpty } from 'common/helpers'
import { getFieldsMap, getValuesFromProps, overrideFieldOptions, extendFieldOptions, getFieldValues,
  linkToState, validateFields, updateEmptyFieldValues, validateForm, modifyOutput, validateField } from './util'


/**
 *
 * @param {Object} options
 * @param {String} options.name
 * @param {Object} options.fields
 * @param {Function} options.initialValues
 */
const decorator = (options) => (ComposedComponent) => {

  class FormComponent extends Component {

    static contextTypes = {
      store: PropTypes.object,
    }

    constructor(props, context) {
      super()

      this.name = options.name
      this.context = context
      this.options = typeof options === 'function' ? options(props) : options

      this.initialValues = {}

      this.state = {
        isSubmitted: false,
        fieldValues: this.configureFieldValues(undefined, this.options),
        validatedFields: {},
      }
    }

    componentDidMount() {
      this._isMounted = true
    }

    componentWillUnmount() {
      this._isMounted = false
    }

    // We no need subscribe to props update
    //
    // componentWillReceiveProps(nextProps) {
    //   let { fieldValues } = this.state
    //
    //   // TODO think how to improve this
    //   fieldValues = this.extendValuesFromProps(nextProps, fieldValues)
    //   fieldValues = updateEmptyFieldValues(this.options.fields, fieldValues)
    //
    //   this.setState({
    //     fieldValues,
    //   })
    // }

    getInitialValues() {
      const { store: { getState } } = this.context

      if (!isNotEmpty(this.initialValues) && typeof this.options.initialValues === 'function') {
        this.initialValues = this.options.initialValues(getState(), this.props)
      }

      return this.initialValues
    }

    // TODO remove callback!
    /*
      Callback is ductape!
      When configure() called and after this call props changed componentWillReceiveProps() will be called!
      In that case `fieldValues` in componentWillReceiveProps() won't update yet... bcs `fieldValues`
      must be updaten in configure() first!
     */
    configure = (options, callback) => {
      const { fieldValues: stateFieldValues } = this.state
      const fieldValues = this.configureFieldValues(stateFieldValues, options)

      this.setState({
        fieldValues,
      }, () => {
        if (typeof callback === 'function') {
          callback()
        }
      })
    }

    configureFieldValues = (stateFieldValues, options) => {
      this.name = options.name
      this.options = {
        ...options,
        fields: overrideFieldOptions(options.fields),
      }

      const fieldOptions = options.fields
      const initialValues = this.getInitialValues()
      let fieldValues

      // TODO think how to improve this
      fieldValues = getFieldValues(fieldOptions)
      fieldValues = object.defaultsDeep(fieldValues, initialValues)

      if (stateFieldValues) {
        fieldValues = object.defaultsDeep(fieldValues, stateFieldValues)
      }

      return updateEmptyFieldValues(options.fields, fieldValues)
    }

    startValidateForm = async () => {
      const { fieldValues }  = this.state
      const fieldsMap        = getFieldsMap(this.options.fields)
      const initialValues    = this.getInitialValues()
      const propValues       = getValuesFromProps(this.props, this.options.fields)

      let initialPropValues  = object.defaultsDeep(object.defaultsDeep(fieldsMap, initialValues), propValues)
      initialPropValues      = updateEmptyFieldValues(this.options.fields, initialPropValues)

      const { isFormValid, validatedFields, groups } = await validateForm(this.options, fieldValues, initialPropValues)

      return {
        validatedFields,
        isFormValid,
        fieldValues,
        groups,
      }
    }

    onSubmit = (callback, errorCallback) => async (event) => {
      if (event) {
        event.preventDefault()
      }

      const { isFormValid, fieldValues, validatedFields, groups } = await this.startValidateForm()

      this.setState({
        isSubmitted: true,
        validatedFields,
      })

      if (isFormValid) {
        callback(fieldValues, groups)
      }
      else if (typeof errorCallback === 'function') {
        errorCallback(fieldValues, groups, validatedFields)
      }
    }

    updateFieldValues = (newValues, callback) => {
      const { fieldValues } = this.state

      const newFieldValues = object.defaultsDeep(fieldValues, newValues)

      this.setState({
        fieldValues: newFieldValues,
      }, callback)
    }

    // This functionality was added for apple pay
    // If user not logged and try buy subscription
    startValidateFields = async (fields) => {
      const validatedFields = {}
      const fieldNames = Object.keys(fields)

      const validatedFieldsMap = await Promise.all(fieldNames.map(async (fieldName) => {

        const error = await validateField(fields[fieldName].validate, fields[fieldName].value)

        return { error, validate: true }
      }))

      fieldNames.forEach((fieldName, index) => {
        validatedFields[fieldName] = validatedFieldsMap[index]
      })

      return validatedFields
    }

    /**
     * validation of the selected fields
     * @param fields
     * @param groupName
     */
    validateFields = ({ fields, groupName = null }) =>
      new Promise(async (resolve) => {
        let validatedFields = await this.startValidateFields(fields)

        if (groupName) {
          validatedFields = {
            [groupName]: validatedFields,
          }
        }

        // use case when this check is needed
        // 0. we have: Input with onBlur method where validateFields() called
        // 1. user click on input field
        // 2. user click on any link on site
        // 3. onBlur is called
        // 4. validateFields is called
        // 5. user redirected on another page (because validateFields is async)
        // 6. form is unmounted
        // 7. this.setState called on unmounted component
        if (this._isMounted) {
          this.setState({
            isSubmitted: true,
            validatedFields,
          }, () => {
            resolve()
          })
        }
      })

    resetValidation = () => {
      this.setState({
        isSubmitted: false,
        validatedFields: {},
      })
    }

    render() {
      const { isSubmitted, fieldValues, validatedFields } = this.state

      if (!fieldValues) {
        return null
      }

      let linkedFields

      linkedFields = linkToState.call(this, this.options.fields, fieldValues)
      linkedFields = extendFieldOptions(this.options.fields, linkedFields)
      linkedFields = isSubmitted ? validateFields(linkedFields, validatedFields, fieldValues) : linkedFields
      linkedFields = modifyOutput(linkedFields)

      return (
        <ComposedComponent
          {...this.props}
          {...this.state}
          fields={linkedFields}
          submitForm={this.onSubmit}
          updateFormOptions={this.configure}
          updateFieldValues={this.updateFieldValues}
          validateFields={this.validateFields}
          resetValidation={this.resetValidation}
        />
      )
    }
  }

  return FormComponent
}

export default decorator
